import { useCallback, useContext } from "react";
import type { NativeBridge } from "@swiggy-private/native-bridge";
import { SwiggyContextHeader } from "@swiggy-private/portal-dineout-context";

import { useIsV2InApp } from "./useIsV2InApp";
import { STORAGE_KEYS } from "../constants/StorageKeys";
import type { IParentOrderMetadata } from "../types";
import { getBridgeSdk } from "../utils";
import { sendCustomNREvWithHeaders } from "../utils/newrelic";

export interface IPinDataSyncPayload {
    status: string;
    pin: string;
    tableNo: string;
    hostName: string;
    restaurantFlow: string;
    message: string;
    parentOrderId: string;
}

export const toPinDataSyncPayload = (metadata: IParentOrderMetadata): IPinDataSyncPayload => ({
    status: (metadata.status ?? "").toUpperCase(),
    pin: metadata.pin ?? "",
    tableNo: metadata.tableNo ?? "",
    hostName: metadata.hostName ?? "",
    restaurantFlow: metadata.restaurantFlow ?? "",
    message: metadata.message ?? "",
    parentOrderId: metadata.parentOrderId ?? "",
});

// digitalMenuPinDataSync ships with a future swiggy-js-bridge release; type it locally
// and call optionally so this compiles and stays a no-op until the bridge is published.
type PinDataSyncBridge = NativeBridge & {
    digitalMenuPinDataSync?: (payload: IPinDataSyncPayload) => Promise<unknown>;
};

// Identity for a pin sync — scoped to the user AND their order, so a different user on the same
// table (same parentOrderId) still syncs. Falls back to pin when the order id is absent.
const syncKey = (userId: string, payload: IPinDataSyncPayload): string =>
    `${userId}|${payload.parentOrderId || payload.pin}`;

// Dedup must survive a webview close/reopen: V2 navigation (navigateToDigitalMenu) sends
// shouldCloseWebview:true, so the Orders webview is destroyed and recreated on each visit — which
// wipes sessionStorage. localStorage persists across reopens, so the sync fires exactly once per
// order. We store the synced order id (not a bare boolean) so a genuinely new order re-syncs.
const isSyncDone = (key: string): boolean => {
    if (typeof window === "undefined" || !key) return false;
    return localStorage.getItem(STORAGE_KEYS.PIN_DATA_SYNC_DONE) === key;
};

const markSyncDone = (key: string): void => {
    if (typeof window === "undefined" || !key) return;
    localStorage.setItem(STORAGE_KEYS.PIN_DATA_SYNC_DONE, key);
};

// Roll back only our own claim — never clear a different order's successful flag.
const clearSyncDone = (key: string): void => {
    if (typeof window === "undefined" || !key) return;
    if (localStorage.getItem(STORAGE_KEYS.PIN_DATA_SYNC_DONE) === key) {
        localStorage.removeItem(STORAGE_KEYS.PIN_DATA_SYNC_DONE);
    }
};

/**
 * Pushes pin data to native via DIGITAL_MENU_PIN_DATA_SYNC.
 *
 * Only fires in-app on V2, and only for a pin-bearing payload — table/host ride along inside that
 * push (native uses them for analytics), so there is no separate pinless push. Deduped to once per
 * user+order via PIN_DATA_SYNC_DONE (localStorage, so it survives the webview close/reopen that V2
 * navigation triggers). Keying on userId as well means a different user on the same table (same
 * parentOrderId) still syncs. Claimed synchronously before the await so concurrent callers (place
 * order, orders page, verify) can't fire duplicate bridge calls.
 */
export const usePinDataSync = () => {
    const { isV2InApp } = useIsV2InApp();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const userId = swiggyHeaders?.userId ?? "";

    const syncPinDataToNative = useCallback(
        async (metadata: IParentOrderMetadata): Promise<void> => {
            const payload = toPinDataSyncPayload(metadata);
            if (!isV2InApp) return;
            // Only a verified/generated pin is worth syncing; table/host travel inside it.
            if (!payload.pin) return;

            const sdk = getBridgeSdk() as PinDataSyncBridge;
            if (!sdk.digitalMenuPinDataSync) return;

            const key = syncKey(userId, payload);
            if (isSyncDone(key)) return;
            // Claim the flag before awaiting so a concurrent caller short-circuits here.
            markSyncDone(key);

            try {
                await sdk.digitalMenuPinDataSync(payload);
            } catch (error) {
                // Release the flag so a later retry can sync again — the push didn't reach native.
                clearSyncDone(key);
                sendCustomNREvWithHeaders("portal_dineout_error_pin_data_sync_failure", {
                    error: (error as Error)?.message ?? "digitalMenuPinDataSync failed",
                });
            }
        },
        [isV2InApp, userId],
    );

    return { syncPinDataToNative };
};
